import React from "react";
import {
  OrganisationQuoteTemplatesFilters,
  OrganisationQuoteTemplatesSorting,
  TemplateSearchFiltersInput,
} from "../../../../clients/graphqlTypes.js";
import { useFiltersAndPagination } from "../../../commons/filters/useFiltersAndPagination.js";
import { CollectionView } from "../../../commons/hooks/useCollectionView.js";
import { useStateWithUrlParam } from "../../../commons/hooks/useStateWithUrlParam.js";
import { PAGE_LIST_RESULTS_PER_PAGE } from "../../../constants.js";
import { DEFAULT_SORTING } from "./TemplatesQuoteFilterModal.js";
import { usePaginationParamsInState } from "../../../commons/pagination/usePaginationParams.js";

interface UseTemplatesQueryVariables<
  Filters extends
    | OrganisationQuoteTemplatesFilters
    | TemplateSearchFiltersInput,
> {
  initialFilters: Filters;
  initialSorting?: OrganisationQuoteTemplatesSorting[];
  initialSearchTerm?: string;
}

export const useTemplatesQueryVariablesInUrl = <
  Filters extends
    | OrganisationQuoteTemplatesFilters
    | TemplateSearchFiltersInput,
>({
  initialFilters,
  initialSorting,
  initialSearchTerm,
  searchUrlParam = "search",
  pageUrlParam = "page",
  perPageUrlParam = "pageSize",
  filtersUrlParam = "filters",
  sortingsUrlParam = "sortings",
}: UseTemplatesQueryVariables<Filters> & {
  searchUrlParam?: string;
  pageUrlParam?: string;
  perPageUrlParam?: string;
  filtersUrlParam?: string;
  sortingsUrlParam?: string;
}) => {
  const {
    limit,
    offset,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<OrganisationQuoteTemplatesSorting, Filters>(
    initialFilters,
    initialSorting ?? DEFAULT_SORTING,
    PAGE_LIST_RESULTS_PER_PAGE,
    pageUrlParam,
    perPageUrlParam,
    filtersUrlParam,
    sortingsUrlParam
  );

  const [searchTerm, setSearchTerm] = useStateWithUrlParam<string>(
    searchUrlParam,
    initialSearchTerm ?? "",
    toRemoveParams
  );

  const QUERY_BASE_VARIABLES = {
    offset,
    limit,
    sorting: initialSorting ?? DEFAULT_SORTING,
  };

  return {
    limit,
    offset,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    queryBaseVariables: QUERY_BASE_VARIABLES,
    toRemoveParams,
    searchTerm,
    setSearchTerm,
  };
};

export const useTemplatesQueryVariablesInState = <
  Filters extends
    | OrganisationQuoteTemplatesFilters
    | TemplateSearchFiltersInput,
>({
  initialFilters,
  initialSorting,
  initialSearchTerm,
}: UseTemplatesQueryVariables<Filters>) => {
  const { offset, limit, paginationModel, setPaginationModel } =
    usePaginationParamsInState({
      perPageDefaultAmount: PAGE_LIST_RESULTS_PER_PAGE,
    });

  const [filters, setFilters] = React.useState<Filters>(initialFilters);
  const resetFilters = React.useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters, setFilters]);

  const [sorting, setSorting] = React.useState<
    OrganisationQuoteTemplatesSorting[]
  >(initialSorting ?? DEFAULT_SORTING);

  const [searchTerm, setSearchTerm] = React.useState<string>(
    initialSearchTerm ?? ""
  );

  const QUERY_BASE_VARIABLES = {
    offset,
    limit,
    sorting: initialSorting ?? DEFAULT_SORTING,
  };

  return {
    limit,
    offset,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    queryBaseVariables: QUERY_BASE_VARIABLES,
    searchTerm,
    setSearchTerm,
  };
};
